import { computed, onBeforeUnmount, onMounted, ref, type CSSProperties, type Ref } from "vue";
import type {
  DSResizeHandleResizeEndDetails,
  DSResizeHandleResizeStartDetails,
} from "@/src/design_system/primitives/resize_handle/types/emits";
import type { DSResizeHandleProps } from "@/src/design_system/primitives/resize_handle/types/props";
import { RTL_DIRECTION } from "@/src/design_system/shared/constants/direction";
import { DS_DIRECTION } from "@/src/design_system/shared/types/direction";
import { DS_PAGE_COLUMNS_NATIVE_EVENT } from "../types/emits";
import type { DSPageColumnsProps } from "../types/props";
import {
  constrainPageColumnValues,
  getPageColumnMinimumValue,
  getPageColumnsGridTemplate,
  getPageColumnsResizeHandles,
  normalizePageColumnValues,
  normalizePageColumnMinimumWidth,
  resizePageColumnPairAtPosition,
  type PageColumnsResizeHandleBounds,
} from "../utils/pageColumns";

interface PointerResizeBaseline {
  columnCount: number;
  handleModelValue: number;
  invalidated: boolean;
  ownerIndex?: number;
  ownerIndexes: number[];
  sourceIndex: number;
  lastPublishedValues?: number[];
  minimumValue: number;
  geometryValues: number[];
  minimumWidth: number;
  modelValues: number[];
  width: number;
}

interface PageColumnsResizeHandle {
  ariaValueText: string;
  max: number;
  min: number;
  modelValue: number;
  step: number;
  style: CSSProperties;
}

interface UseDSPageColumnsOptions {
  props: Readonly<DSPageColumnsProps>;
  columnCount: Readonly<Ref<number>>;
  rootId: Readonly<Ref<string>>;
  rootRef: Ref<HTMLElement | undefined>;
  onFocusTransfer: (sourceIndex: number, ownerIndex: number) => void;
  onUpdate: (value: number[]) => void;
}

const RESIZE_HANDLE_POSITION_PROPERTY = "--ds-page-columns-resize-handle-position";
const PERCENTAGE_SCALE = 100;
const DEFAULT_VISUAL_WIDTH = 100;

const haveEqualValues = (left: number[], right: number[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const percentageToPixels = (percentage: number, width: number) => (percentage / PERCENTAGE_SCALE) * width;

const percentageValueText = (percentage: number) => `${Number(percentage.toFixed(2))}%`;

const applyPointerBaseline = (
  handles: PageColumnsResizeHandle[],
  percentageHandles: PageColumnsResizeHandleBounds[],
  baseline: PointerResizeBaseline
) => {
  const { ownerIndex, sourceIndex } = baseline;
  if (ownerIndex !== undefined && ownerIndex !== sourceIndex) {
    [handles[sourceIndex], handles[ownerIndex]] = [handles[ownerIndex], handles[sourceIndex]];
  }

  const ownerIndexes = ownerIndex === undefined ? baseline.ownerIndexes : [ownerIndex];
  const baselineHandles = getPageColumnsResizeHandles(baseline.geometryValues, baseline.minimumValue);
  const ownerHandles = ownerIndexes.flatMap(index => (baselineHandles[index] ? [baselineHandles[index]] : []));
  const activeHandle = handles[sourceIndex];
  if (!activeHandle || ownerHandles.length === 0) return;

  const startPercentage = ownerHandles[0].value;
  const currentPercentage =
    ownerIndex === undefined ? startPercentage : (percentageHandles[ownerIndex]?.value ?? startPercentage);
  activeHandle.ariaValueText = percentageValueText(currentPercentage);
  activeHandle.modelValue =
    baseline.handleModelValue + percentageToPixels(currentPercentage - startPercentage, baseline.width);
  activeHandle.min =
    baseline.handleModelValue +
    percentageToPixels(Math.min(...ownerHandles.map(handle => handle.min)) - startPercentage, baseline.width);
  activeHandle.max =
    baseline.handleModelValue +
    percentageToPixels(Math.max(...ownerHandles.map(handle => handle.max)) - startPercentage, baseline.width);
  activeHandle.step = baseline.width / PERCENTAGE_SCALE;
};

export const useDSPageColumns = ({
  props,
  columnCount,
  rootId,
  rootRef,
  onFocusTransfer,
  onUpdate,
}: UseDSPageColumnsOptions) => {
  const pointerResizeBaseline = ref<PointerResizeBaseline>();
  const visualWidth = ref(DEFAULT_VISUAL_WIDTH);
  let resizeObserver: ResizeObserver | undefined;
  const columnValues = computed(() => normalizePageColumnValues(props.modelValue, columnCount.value));
  const minimumColumnWidth = computed(() => normalizePageColumnMinimumWidth(props.minColumnWidth));
  const layoutWidth = computed(() => Math.max(visualWidth.value, columnCount.value * minimumColumnWidth.value));
  const layoutColumnValues = computed(() =>
    constrainPageColumnValues(columnValues.value, layoutWidth.value, minimumColumnWidth.value)
  );
  const minimumColumnValue = computed(() =>
    getPageColumnMinimumValue(layoutColumnValues.value, layoutWidth.value, minimumColumnWidth.value)
  );
  const gridStyle = computed<CSSProperties>(() => ({
    gridTemplateColumns: getPageColumnsGridTemplate(layoutColumnValues.value, minimumColumnWidth.value),
    minWidth: minimumColumnWidth.value > 0 ? `${columnCount.value * minimumColumnWidth.value}px` : undefined,
  }));
  const resizeHandleProps = computed<Pick<DSResizeHandleProps, "ariaControls" | "ariaLabel" | "direction">>(() => ({
    ariaControls: rootId.value,
    ariaLabel: props.resizeHandleAriaLabel,
    direction: DS_DIRECTION.Vertically,
  }));
  const resizeHandles = computed<PageColumnsResizeHandle[]>(() => {
    const percentageHandles = getPageColumnsResizeHandles(layoutColumnValues.value, minimumColumnValue.value);
    const handles = percentageHandles.map(handle => {
      const collocatedHandles = percentageHandles.filter(candidate => candidate.value === handle.value);
      return {
        ariaValueText: percentageValueText(handle.value),
        max: percentageToPixels(Math.max(...collocatedHandles.map(candidate => candidate.max)), layoutWidth.value),
        min: percentageToPixels(Math.min(...collocatedHandles.map(candidate => candidate.min)), layoutWidth.value),
        modelValue: percentageToPixels(handle.value, layoutWidth.value),
        step: layoutWidth.value / PERCENTAGE_SCALE,
        style: { [RESIZE_HANDLE_POSITION_PROPERTY]: `${handle.value}%` },
      };
    });
    const baseline = pointerResizeBaseline.value;
    if (baseline) applyPointerBaseline(handles, percentageHandles, baseline);
    return handles;
  });

  const publishModelValue = (value: number[]) => {
    onUpdate(value);
    rootRef.value?.dispatchEvent(
      new CustomEvent(DS_PAGE_COLUMNS_NATIVE_EVENT.Update, {
        detail: { value },
        bubbles: true,
        composed: true,
      })
    );
  };

  const inlineDirectionMultiplier = () =>
    rootRef.value && getComputedStyle(rootRef.value).direction === RTL_DIRECTION ? -1 : 1;

  const rootWidth = () => {
    const width = rootRef.value?.getBoundingClientRect().width ?? 0;
    return Math.max(width > 0 ? width : DEFAULT_VISUAL_WIDTH, columnCount.value * minimumColumnWidth.value);
  };

  const publishChangedPointerValue = (baseline: PointerResizeBaseline, value: number[]) => {
    const currentValue = baseline.lastPublishedValues ?? baseline.modelValues;
    if (haveEqualValues(value, currentValue)) return;

    publishModelValue(value);
    baseline.lastPublishedValues = value;
  };

  const hasPointerManagedValue = (baseline: PointerResizeBaseline) => {
    if (props.modelValue.length !== baseline.columnCount) return false;

    const currentValues = normalizePageColumnValues(props.modelValue, baseline.columnCount);
    return (
      haveEqualValues(currentValues, baseline.modelValues) ||
      Boolean(baseline.lastPublishedValues && haveEqualValues(currentValues, baseline.lastPublishedValues))
    );
  };

  const canContinuePointerResize = (baseline: PointerResizeBaseline) => {
    if (baseline.invalidated) return false;
    if (hasPointerManagedValue(baseline)) return true;

    baseline.invalidated = true;
    return false;
  };

  const cancelPointerResize = () => {
    const baseline = pointerResizeBaseline.value;
    if (!baseline) return;

    if (
      hasPointerManagedValue(baseline) &&
      baseline.lastPublishedValues &&
      !haveEqualValues(baseline.lastPublishedValues, baseline.modelValues)
    ) {
      publishModelValue(baseline.modelValues);
    }
    pointerResizeBaseline.value = undefined;
  };

  const updateVisualWidth = () => {
    const width = rootWidth();
    if (width === visualWidth.value) return;

    visualWidth.value = width;
  };

  const isResizeHandleDisabled = (index: number) => {
    const baseline = pointerResizeBaseline.value;
    const handle = resizeHandles.value[index];
    const isLocked = handle && Math.abs(handle.max - handle.min) < 1e-8;
    return Boolean((baseline && baseline.sourceIndex !== index) || isLocked);
  };

  const onResizeHandleNativeStart = (event: Event) => {
    if (!pointerResizeBaseline.value || !(event.target instanceof HTMLElement)) return;
    if (event.target.parentElement !== rootRef.value) return;

    event.preventDefault();
    event.stopPropagation();
  };

  const getPointerOwnerIndexes = (pointerCoordinate: number, values: number[], width: number): number[] => {
    const root = rootRef.value;
    if (!root) return [];

    const { left: rootStart } = root.getBoundingClientRect();

    const directionMultiplier = inlineDirectionMultiplier();
    const centers = getPageColumnsResizeHandles(values).map(handle => {
      const physicalPosition = directionMultiplier === 1 ? handle.value : PERCENTAGE_SCALE - handle.value;
      return rootStart + (physicalPosition / PERCENTAGE_SCALE) * width;
    });
    const distances = centers.map(center => Math.abs(center - pointerCoordinate));
    const nearestDistance = Math.min(...distances);
    const nearestIndexes = distances.flatMap((distance, index) => (distance === nearestDistance ? [index] : []));
    const nearestCenters = new Set(nearestIndexes.map(index => centers[index]));

    return nearestCenters.size === 1 ? nearestIndexes : nearestIndexes.slice(0, 1);
  };

  const onResizeStart = (index: number, details: DSResizeHandleResizeStartDetails) => {
    if (pointerResizeBaseline.value) return;

    const width = rootWidth();
    visualWidth.value = width;
    const geometryValues = [...layoutColumnValues.value];
    const ownerIndexes = getPointerOwnerIndexes(details.coordinate, geometryValues, width);

    pointerResizeBaseline.value = {
      columnCount: geometryValues.length,
      geometryValues,
      handleModelValue: details.value,
      invalidated: false,
      minimumValue: getPageColumnMinimumValue(geometryValues, width, minimumColumnWidth.value),
      minimumWidth: minimumColumnWidth.value,
      modelValues: [...columnValues.value],
      ownerIndex: ownerIndexes.length === 1 ? ownerIndexes[0] : undefined,
      ownerIndexes,
      sourceIndex: index,
      width,
    };
  };

  const onKeyboardResize = (index: number, modelValue: number) => {
    publishModelValue(
      resizePageColumnPairAtPosition(columnValues.value, {
        index,
        minColumnWidth: minimumColumnWidth.value,
        position: (modelValue / layoutWidth.value) * PERCENTAGE_SCALE,
        width: layoutWidth.value,
      })
    );
  };

  const getPointerResizeBaseline = (index: number): PointerResizeBaseline | undefined => {
    const baseline = pointerResizeBaseline.value;
    if (!baseline || baseline.sourceIndex !== index) return;
    if (baseline.columnCount !== layoutColumnValues.value.length) {
      cancelPointerResize();
      return;
    }
    if (!canContinuePointerResize(baseline)) return;

    return baseline;
  };

  const onPointerResize = (index: number, modelValue: number) => {
    const baseline = getPointerResizeBaseline(index);
    if (!baseline) return;

    const logicalDelta = modelValue - baseline.handleModelValue;
    if (baseline.ownerIndex === undefined) {
      if (logicalDelta === 0 || baseline.ownerIndexes.length === 0) return;

      baseline.ownerIndex =
        logicalDelta < 0 ? baseline.ownerIndexes[0] : baseline.ownerIndexes[baseline.ownerIndexes.length - 1];
    }

    const ownerIndex = baseline.ownerIndex;
    if (ownerIndex === undefined) return;

    const ownerHandle = getPageColumnsResizeHandles(baseline.geometryValues, baseline.minimumValue)[ownerIndex];
    if (!ownerHandle) return;

    const targetPosition = ownerHandle.value + (logicalDelta / baseline.width) * PERCENTAGE_SCALE;
    const value = resizePageColumnPairAtPosition(baseline.modelValues, {
      index: ownerIndex,
      minColumnWidth: baseline.minimumWidth,
      position: targetPosition,
      width: baseline.width,
    });
    publishChangedPointerValue(baseline, value);
  };

  const onUpdateModelValue = (index: number, modelValue: number) => {
    if (pointerResizeBaseline.value) onPointerResize(index, modelValue);
    else onKeyboardResize(index, modelValue);
  };

  const onResizeEnd = (index: number, details: DSResizeHandleResizeEndDetails) => {
    const baseline = getPointerResizeBaseline(index);
    if (!baseline) {
      const activeBaseline = pointerResizeBaseline.value;
      if (activeBaseline?.sourceIndex === index && activeBaseline.invalidated) {
        pointerResizeBaseline.value = undefined;
      }
      return;
    }

    if (details.canceled) {
      cancelPointerResize();
      return;
    }

    pointerResizeBaseline.value = undefined;
    if (baseline.ownerIndex !== undefined && baseline.ownerIndex !== baseline.sourceIndex) {
      onFocusTransfer(baseline.sourceIndex, baseline.ownerIndex);
    }
  };

  onMounted(() => {
    updateVisualWidth();
    if (!rootRef.value || typeof ResizeObserver === "undefined") return;

    resizeObserver = new ResizeObserver(updateVisualWidth);
    resizeObserver.observe(rootRef.value);
  });

  onBeforeUnmount(() => resizeObserver?.disconnect());

  return {
    cancelPointerResize,
    gridStyle,
    isResizeHandleDisabled,
    onResizeHandleNativeStart,
    resizeHandleProps,
    resizeHandles,
    onResizeEnd,
    onResizeStart,
    onUpdateModelValue,
  };
};
